import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AiManagementFilePreview from 'components/atoms/ai_management_file_preview';
import BiddingNoContentError from 'components/atoms/bidding_no_content_error';
import Loader from 'components/atoms/loader';
import LoadingBidding from 'components/atoms/loading_bidding';
import { BIDDING_SUMMARY_ACCORDION_SECTIONS } from 'components/molecules/bidding_summary_accordion_sections';
import {
  biddingSummaryPanelId,
  DEFAULT_SUMMARY_FIELD_EMPTY,
  INITIAL_ACCORDION_OPEN,
  type AccordionKey,
} from 'components/molecules/bidding_summary_constants';
import {
  getDaysUntilIsoDate,
  hasExigenciasEspecificas,
  valorDetalhesTexto,
} from 'components/molecules/bidding_summary_utils';
import BiddingSummaryCustomAnalysis, {
  CustomizeAnalysisButton,
} from 'components/molecules/bidding_summary_custom_analysis';
import BiddingSummaryBidderReportDrawer, {
  BidderReportSelection,
} from 'components/molecules/bidding_summary_bidder_report_drawer';
import {
  buildBidderReportData,
  downloadBidderReportPdf,
} from 'components/molecules/bidding_summary_bidder_report_pdf';
import AiManagementBiddingAskEdital from 'components/molecules/ai_management_bidding_ask_edital';
import AiManagementStrategicQuestions from 'components/molecules/ai_management_strategic_questions';
import BiddingSummaryCustomAnalysisDrawer from 'components/molecules/bidding_summary_custom_analysis_drawer';
import SummaryAccordionBlock from 'components/molecules/summary_accordion_block';
import SummaryStatCard from 'components/molecules/summary_stat_card';
import { Container as BiddingSummaryStyles } from 'components/organisms/bidding_summary/styles';

import { ApplicationState } from 'store';
import { BiddingData } from 'store/modules/bidding_store/types';
import {
  deleteBidderConsideration,
  fetchBidderConsideration,
  fetchBidderConsiderationTeam,
  fetchBiddingSummary,
  saveBidderConsideration,
} from 'store/modules/bidding_summary_store/actions';
import {
  BiddingSummaryState,
  BiddingSummaryTypes,
} from 'store/modules/bidding_summary_store/types';
import { ProfileState } from 'store/modules/profile_store/types';

import iconDataDaSessao from 'assets/images/icon_data_da_sessao.svg';
import iconModalidade from 'assets/images/icon_modalidade.svg';
import iconStarredSeetSummary from 'assets/images/icon_starred_seet_summary.svg';
import iconValor from 'assets/images/icon_valor.svg';

import { useCustomAnalysisPrompt } from 'hooks/useCustomAnalysisPrompt';
import { useCustomAnalysisResult } from 'hooks/useCustomAnalysisResult';
import { EditalFile } from 'hooks/useEditalFiles';
import { BidderFileGeneratedHandler } from 'services/actions/ai_management_files';
import { formatIsoDateBr } from 'services/format_iso_date_br';
import tabId from 'services/tabId';
import { Container } from './styles';

const PREVIEWABLE_FILE = /\.(pdf|png|jpe?g|gif|bmp|webp)$/i;

interface AiManagementBiddingAnalysisProps {
  bidding: BiddingData | null;
  loading: boolean;
  error: boolean;
  files: EditalFile[];
  filesLoading: boolean;
  filesError: boolean;
  onFileGenerated: BidderFileGeneratedHandler;
}

const AiManagementBiddingAnalysis: React.FC<AiManagementBiddingAnalysisProps> = ({
  bidding,
  loading,
  error,
  files,
  filesLoading,
  filesError,
  onFileGenerated,
}) => {
  const dispatch = useDispatch();
  const [accordionOpen, setAccordionOpen] =
    useState<Record<AccordionKey, boolean>>(INITIAL_ACCORDION_OPEN);
  const [selectedFileKey, setSelectedFileKey] = useState<string | null>(null);
  const [customAnalysisOpen, setCustomAnalysisOpen] = useState(true);
  const [customAnalysisDrawerOpen, setCustomAnalysisDrawerOpen] = useState(false);
  const [bidderReportDrawerOpen, setBidderReportDrawerOpen] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const {
    prompt: customAnalysisPrompt,
    loading: customAnalysisLoading,
    error: customAnalysisError,
    refetch: refetchCustomAnalysis,
  } = useCustomAnalysisPrompt();

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const { bidding_summary: biddingSummary, request } = useSelector<
    ApplicationState,
    BiddingSummaryState
  >((state) => state.session.bidding_summary);

  const biddingId = bidding ? Number(bidding.bidding_id) || Number(bidding.id) : 0;

  const isAlfaTester = profile?.alfa_tester === true;

  const {
    content: customAnalysisAnswer,
    loading: customAnalysisAnswerLoading,
    refreshing: customAnalysisAnswerRefreshing,
    error: customAnalysisAnswerError,
    refresh: refreshCustomAnalysisAnswer,
  } = useCustomAnalysisResult(
    biddingId,
    isAlfaTester && Boolean(customAnalysisPrompt),
    customAnalysisPrompt?.updatedAt ?? undefined,
  );

  useEffect(() => {
    if (!biddingId) return;

    dispatch({ type: BiddingSummaryTypes.FOCUS_BIDDING_SUMMARY, biddingId });
    dispatch(fetchBiddingSummary(biddingId));
    dispatch(fetchBidderConsideration(biddingId));
    dispatch(fetchBidderConsiderationTeam(biddingId));
  }, [biddingId]);

  const data = biddingSummary?.bidding_summary_data?.[tabId()];
  const summary = data?.summary;
  const bidderConsideration = biddingSummary?.bidder_consideration_data?.[tabId()];
  const bidderConsiderationTeam = biddingSummary?.bidder_consideration_team_data?.[tabId()] ?? [];

  const fetchStatus = request.fetch_bidding_summary?.[tabId()];
  const fetchBidderConsiderationStatus = request.fetch_bidder_consideration?.[tabId()];
  const fetchBidderConsiderationTeamStatus = request.fetch_bidder_consideration_team?.[tabId()];
  const saveBidderConsiderationStatus = request.save_bidder_consideration?.[tabId()];
  const deleteBidderConsiderationStatus = request.delete_bidder_consideration?.[tabId()];
  const isLoadingSummary = Boolean(fetchStatus?.loading);

  const capag = useMemo(() => {
    const publicBody = bidding?.public_body;
    if (!publicBody?.tem_capag || !publicBody.capag) return undefined;

    return publicBody.capag.capag === 'N.D.' ? undefined : publicBody.capag;
  }, [bidding]);

  const accordionSections = useMemo(
    () =>
      BIDDING_SUMMARY_ACCORDION_SECTIONS.filter(
        (section) =>
          (section.key !== 'seguroGarantia' || Boolean(summary?.garantias)) &&
          (section.key !== 'exigenciasEspecificas' ||
            (profile?.alfa_tester === true &&
              hasExigenciasEspecificas(summary?.exigencias_especificas))) &&
          (section.key !== 'questionamentos' ||
            !(
              Array.isArray(summary?.possibilidades_questionamentos) &&
              summary.possibilidades_questionamentos.length === 0
            )),
      ),
    [
      profile?.alfa_tester,
      summary?.garantias,
      summary?.exigencias_especificas,
      summary?.possibilidades_questionamentos,
    ],
  );

  const bidderConsiderationErrorMessage = useMemo(() => {
    if (saveBidderConsiderationStatus?.error) {
      return 'Não foi possível salvar suas considerações. Tente novamente.';
    }
    if (deleteBidderConsiderationStatus?.error) {
      return 'Não foi possível excluir suas considerações. Tente novamente.';
    }
    if (fetchBidderConsiderationStatus?.error) {
      return 'Não foi possível carregar suas considerações. Tente novamente.';
    }
    if (fetchBidderConsiderationTeamStatus?.error) {
      return 'Não foi possível carregar as considerações da equipe. Tente novamente.';
    }
    return undefined;
  }, [
    saveBidderConsiderationStatus?.error,
    deleteBidderConsiderationStatus?.error,
    fetchBidderConsiderationStatus?.error,
    fetchBidderConsiderationTeamStatus?.error,
  ]);

  const previewableFiles = useMemo(
    () => files.filter((file) => PREVIEWABLE_FILE.test(file.name)),
    [files],
  );

  useEffect(() => {
    setSelectedFileKey(previewableFiles[0]?.key ?? null);
  }, [previewableFiles]);

  const selectedFile = previewableFiles.find((file) => file.key === selectedFileKey);

  const toggleAccordion = (key: AccordionKey) =>
    setAccordionOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleCustomizeAnalysis = () => setCustomAnalysisDrawerOpen(true);
  const handleGenerateBidderFile = () => setBidderReportDrawerOpen(true);

  const handleGenerateReport = async (selection: BidderReportSelection) => {
    if (isGeneratingReport) return;

    setIsGeneratingReport(true);

    const numeroLicitacao = summary?.numero_licitacao?.trim();
    const filenameBase = numeroLicitacao
      ? `Relatório do Licitante ${numeroLicitacao} - Conlicitação ${biddingId}`
      : `Relatório do Licitante - Conlicitação ${biddingId}`;

    const generated = await downloadBidderReportPdf(
      buildBidderReportData({
        summary: summary ?? null,
        biddingNumber: numeroLicitacao || String(biddingId),
        publicBody: bidding?.public_body?.nome?.trim() || summary?.orgao_responsavel?.trim() || '',
        conlicitacaoId: String(biddingId),
        withAnalysis: selection.biddingAnalysis,
        bidderConsideration: bidderConsideration?.consideration || '',
        bidderConsiderationTeam,
        withConsideration: selection.bidderConsideration,
        questions: selection.questions,
      }),
      filenameBase,
    );

    setIsGeneratingReport(false);

    if (!generated) {
      window.alert('Não foi possível gerar o relatório. Tente novamente.');
      return;
    }

    setBidderReportDrawerOpen(false);
    onFileGenerated('relatorio_licitante', generated.blob, generated.filename);
  };

  const accordionCtx = summary
    ? {
        summary,
        biddingId,
        bidding,
        defaultEmptyValue: DEFAULT_SUMMARY_FIELD_EMPTY,
        isLegacyGarantiaFormat: Boolean(summary?.garantia?.trim()),
        capag,
        bidderConsideration: bidderConsideration?.consideration || '',
        bidderConsiderationTeam,
        isLoadingBidderConsideration: Boolean(fetchBidderConsiderationStatus?.loading),
        isSavingBidderConsideration: Boolean(saveBidderConsiderationStatus?.loading),
        isDeletingBidderConsideration: Boolean(deleteBidderConsiderationStatus?.loading),
        bidderConsiderationErrorMessage,
        onSaveBidderConsideration: (consideration: string) =>
          dispatch(saveBidderConsideration(biddingId, consideration)),
        onDeleteBidderConsideration: () => dispatch(deleteBidderConsideration(biddingId)),
      }
    : null;

  const renderContent = () => {
    if (loading || isLoadingSummary) {
      return (
        <div className="d-flex align-items-center justify-content-center py-5">
          <div className="position-relative">
            <LoadingBidding
              title="Carregando a análise do edital."
              subtitle="Aguarde enquanto buscamos as informações."
            />
          </div>
        </div>
      );
    }

    if (error || !bidding || !summary || !accordionCtx) {
      return (
        <div className="d-flex align-items-center justify-content-center py-5">
          <div className="position-relative">
            <BiddingNoContentError
              title="Não foi possível carregar a análise do edital desta licitação."
              subtitle="Tente novamente mais tarde."
            />
          </div>
        </div>
      );
    }

    return (
      <div className="bidding-summary-inner">
        <div className="row summary-stat-row">
          <SummaryStatCard
            iconSrc={iconValor}
            label="Valor Estimado"
            value={valorDetalhesTexto(summary)}
          />
          <SummaryStatCard
            iconSrc={iconModalidade}
            label="Modalidade"
            value={summary.tipo_contratacao?.trim() || summary.modo_disputa?.trim() || '—'}
            badge={
              summary.tipo_contratacao?.trim() &&
              summary.modo_disputa?.trim() &&
              summary.tipo_contratacao.trim() !== summary.modo_disputa.trim() ? (
                <span className="badge badge-modo-disputa mt-2">{summary.modo_disputa}</span>
              ) : undefined
            }
          />
          <SummaryStatCard
            iconSrc={iconDataDaSessao}
            label="Data da Sessão"
            value={formatIsoDateBr(summary.data_sessao_publica) || '—'}
            badge={(() => {
              const dias = getDaysUntilIsoDate(summary.data_sessao_publica);
              if (dias === null || dias < 0) return undefined;
              return (
                <span className="badge badge-sessao-dias mt-2">
                  Em {dias} {dias === 1 ? 'dia' : 'dias'}
                </span>
              );
            })()}
          />
        </div>

        <div className="mt-3">
          <SummaryAccordionBlock
            open={customAnalysisOpen}
            onToggle={() => setCustomAnalysisOpen((prev) => !prev)}
            accordionPanelId="bidding-summary-panel-analise-personalizada"
            title="Análise personalizada"
            iconSrc={iconStarredSeetSummary}
          >
            <BiddingSummaryCustomAnalysis
              prompt={customAnalysisPrompt}
              isLoading={customAnalysisLoading}
              hasError={customAnalysisError}
              onCustomize={handleCustomizeAnalysis}
              isAlfaTester={isAlfaTester}
              answer={customAnalysisAnswer}
              isAnswerLoading={customAnalysisAnswerLoading}
              isAnswerRefreshing={customAnalysisAnswerRefreshing}
              hasAnswerError={customAnalysisAnswerError}
              onRefreshAnswer={refreshCustomAnalysisAnswer}
            />
          </SummaryAccordionBlock>

          {accordionSections.map((section) => {
            if (section.key === 'exigenciasEspecificas') {
              return (
                <React.Fragment key={section.key}>{section.render(accordionCtx)}</React.Fragment>
              );
            }

            const iconProps = section.iconSrc
              ? { iconSrc: section.iconSrc }
              : { icon: section.icon || 'article' };

            return (
              <React.Fragment key={section.key}>
                {section.key === 'consideracoesLicitante' && isAlfaTester && biddingId ? (
                  <AiManagementStrategicQuestions biddingId={biddingId} />
                ) : null}
                <SummaryAccordionBlock
                  open={accordionOpen[section.key]}
                  onToggle={() => toggleAccordion(section.key)}
                  accordionPanelId={biddingSummaryPanelId(section.key)}
                  title={section.title}
                  cardClassName={section.cardClassName}
                  headerClassName={section.headerClassName}
                  {...iconProps}
                >
                  {section.render(accordionCtx)}
                </SummaryAccordionBlock>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Container className="d-flex flex-column">
      <div className="analysis-header d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
        <h4 className="mb-0">Análise do edital</h4>

        <div className="analysis-header-actions d-flex align-items-center flex-shrink-0">
          <CustomizeAnalysisButton onClick={handleCustomizeAnalysis} />

          <button
            type="button"
            className="btn generate-bidder-file-button d-inline-flex align-items-center"
            onClick={handleGenerateBidderFile}
          >
            <span className="material-icons" aria-hidden>
              note_add
            </span>
            Gerar arquivo do licitante
          </button>
        </div>
      </div>

      <div className="analysis-columns d-flex flex-grow-1">
        <div className="analysis-main d-flex flex-column flex-grow-1">
          <BiddingSummaryStyles className="analysis-body flex-grow-1 overflow-auto px-4 pt-4 pb-4">
            {renderContent()}
          </BiddingSummaryStyles>
        </div>

        {(filesLoading || filesError || previewableFiles.length > 0) && (
          <aside className="analysis-preview flex-shrink-0 border-left d-flex flex-column">
            {previewableFiles.length > 0 && (
              <div className="p-3 border-bottom">
                <label className="text-muted small mb-0 d-block" htmlFor="analysis-preview-file">
                  Arquivo do edital
                  <select
                    id="analysis-preview-file"
                    className="form-control mt-1"
                    value={selectedFileKey ?? ''}
                    onChange={(event) => setSelectedFileKey(event.target.value)}
                  >
                    {previewableFiles.map((file) => (
                      <option key={file.key} value={file.key}>
                        {file.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}

            <div className="analysis-preview-body flex-grow-1 overflow-auto p-3">
              {filesLoading && (
                <div className="d-flex justify-content-center py-5">
                  <Loader />
                </div>
              )}

              {!filesLoading && filesError && (
                <div className="alert alert-warning mb-0" role="alert">
                  Não foi possível carregar os arquivos do edital.
                </div>
              )}

              {!filesLoading && selectedFile && (
                <AiManagementFilePreview
                  key={selectedFile.key}
                  name={selectedFile.name}
                  downloadUrl={selectedFile.downloadUrl}
                />
              )}
            </div>
          </aside>
        )}
      </div>

      {bidderReportDrawerOpen && (
        <BiddingSummaryBidderReportDrawer
          isGenerating={isGeneratingReport}
          onClose={() => setBidderReportDrawerOpen(false)}
          onGenerate={handleGenerateReport}
        />
      )}

      {biddingId > 0 && <AiManagementBiddingAskEdital biddingId={biddingId} />}

      {customAnalysisDrawerOpen && (
        <BiddingSummaryCustomAnalysisDrawer
          prompt={customAnalysisPrompt}
          onClose={() => setCustomAnalysisDrawerOpen(false)}
          onSaved={refetchCustomAnalysis}
        />
      )}
    </Container>
  );
};

export default AiManagementBiddingAnalysis;
